import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import LoadSpinner from "../../../utilities/LoadSpinner";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { getDefaultAxiosConfig } from "../../../utilities/requests";
import {
  API_get_store_team_members,
  API_subscribe_live_labor_alerts,
  API_unsubscribe_live_labor_alerts,
  API_get_live_labor_subscriptions,
} from "../../../constants";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 440,
  maxHeight: "80vh",
  bgcolor: "background.paper",
  border: "2px solid #E2E8F0",
  borderRadius: "15px",
  boxShadow: 24,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

function LiveLaborAlertToggle({ storeId }) {

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [serverStmIds, setServerStmIds] = useState(new Set());
  const [selectedStmIds, setSelectedStmIds] = useState(new Set());
  const [myStmId, setMyStmId] = useState(null);

  const hasChanges = React.useMemo(() => {
    if (serverStmIds.size !== selectedStmIds.size) return true;
    for (const id of serverStmIds) {
      if (!selectedStmIds.has(id)) return true;
    }
    return false;
  }, [serverStmIds, selectedStmIds]);

  const fetchData = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    try {
      const [empRes, subRes] = await Promise.all([
        axios.get(
          API_get_store_team_members + storeId,
          getDefaultAxiosConfig()
        ),
        axios.get(
          API_get_live_labor_subscriptions + storeId,
          getDefaultAxiosConfig()
        ),
      ]);
      const empList = (empRes.data || []).filter(
        (e) => e.status !== "archived"
      );
      empList.sort((a, b) =>
        `${a.first_name} ${a.last_name}`.localeCompare(
          `${b.first_name} ${b.last_name}`
        )
      );
      setEmployees(empList);

      const subs = subRes.data?.subscriptions || [];
      const subIds = new Set(subs.map((s) => s.store_team_member_id));
      setServerStmIds(subIds);
      setSelectedStmIds(new Set(subIds));
      setMyStmId(subRes.data?.my_store_team_member_id ?? null);
    } catch (e) {
      console.error("Failed to fetch subscription data", e);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, fetchData]);

  const handleToggleCheckbox = (stmId) => {
    if (saving) return;
    setSelectedStmIds((prev) => {
      const next = new Set(prev);
      if (next.has(stmId)) {
        next.delete(stmId);
      } else {
        next.add(stmId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!storeId || saving) return;
    setSaving(true);
    try {
      const toSubscribe = [...selectedStmIds].filter(
        (id) => !serverStmIds.has(id)
      );
      const toUnsubscribe = [...serverStmIds].filter(
        (id) => !selectedStmIds.has(id)
      );

      const requests = [
        ...toSubscribe.map((stmId) =>
          axios.post(
            API_subscribe_live_labor_alerts,
            { store_id: storeId, store_team_member_id: stmId },
            getDefaultAxiosConfig()
          )
        ),
        ...toUnsubscribe.map((stmId) =>
          axios.post(
            API_unsubscribe_live_labor_alerts,
            { store_id: storeId, store_team_member_id: stmId },
            getDefaultAxiosConfig()
          )
        ),
      ];

      await Promise.all(requests);
      setServerStmIds(new Set(selectedStmIds));
      setOpen(false);
    } catch (e) {
      console.error("Failed to save subscriptions", e);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setOpen(false);
    }
  };

  const otherEmployees = employees.filter((emp) => emp.id !== myStmId);

  return (
    <>
      <button
        className="px-4 py-2 rounded-lg cursor-pointer flex items-center gap-1.5 bg-white border border-slate-200 hover:border-violet-400 text-sm font-semibold text-slate-600 hover:text-violet-700 transition-colors whitespace-nowrap"
        onClick={() => setOpen(true)}
      >
        <NotificationsNoneIcon sx={{ fontSize: 18 }} />
        Subscribe
      </button>

      <Modal open={open} onClose={handleClose}>
        <Box sx={modalStyle}>
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
            <h2 className="text-lg font-bold text-slate-800">
              Daily Labor Alert Subscriptions
            </h2>
            <IconButton onClick={handleClose} size="small" disabled={saving}>
              <CloseIcon />
            </IconButton>
          </div>

          {/* Description */}
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
            <p className="text-sm text-slate-600">
              Selected employees will receive push notifications for
              alerts for today only.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadSpinner />
            </div>
          ) : (
            <>
              {/* Myself row */}
              {myStmId && (
                <div
                  className="flex items-center px-5 py-3 border-b border-slate-200 cursor-pointer hover:bg-violet-50"
                  onClick={() => handleToggleCheckbox(myStmId)}
                >
                  <Checkbox
                    checked={selectedStmIds.has(myStmId)}
                    sx={{
                      color: "#6d28d9",
                      "&.Mui-checked": { color: "#6d28d9" },
                    }}
                  />
                  <span className="text-base font-semibold text-slate-800">
                    Myself
                  </span>
                </div>
              )}

              {/* Employee list */}
              <div className="overflow-auto flex-1 px-2 py-2">
                {otherEmployees.map((emp) => (
                  <div
                    key={emp.id}
                    className="flex items-center px-3 py-2 rounded-lg cursor-pointer hover:bg-slate-100"
                    onClick={() => handleToggleCheckbox(emp.id)}
                  >
                    <Checkbox
                      checked={selectedStmIds.has(emp.id)}
                      sx={{
                        color: "#6d28d9",
                        "&.Mui-checked": { color: "#6d28d9" },
                      }}
                    />
                    <span className="text-sm text-slate-800">
                      {emp.first_name} {emp.last_name}
                    </span>
                  </div>
                ))}
                {otherEmployees.length === 0 && !myStmId && (
                  <p className="text-sm text-slate-500 text-center py-6">
                    No employees found
                  </p>
                )}
              </div>

              {/* Footer with Save button */}
              <div className="flex items-center justify-end px-5 py-3 border-t border-slate-200">
                <button
                  className={`px-6 py-2 rounded-md text-white font-semibold ${
                    hasChanges && !saving
                      ? "bg-violet-700 hover:bg-violet-900 cursor-pointer"
                      : "bg-slate-300 cursor-not-allowed"
                  }`}
                  onClick={handleSave}
                  disabled={!hasChanges || saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </>
          )}
        </Box>
      </Modal>
    </>
  );
}

export default LiveLaborAlertToggle;
